<template>
  <div v-if="clientId && !inApp" class="google-signin">
    <div ref="button" class="google-button"></div>
    <p v-if="error" class="google-error" role="alert">{{ error }}</p>
    <div class="google-or"><span>ou com e-mail</span></div>
  </div>
</template>

<script>
import { track, getAttribution } from "@/utils/analytics";
import { isInAppBrowser } from "@/utils/appInstall";

let scriptPromise = null;
function loadGoogle() {
  if (window.google && window.google.accounts) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.onload = resolve;
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("gsi"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// "Continuar com Google" (Google Identity Services): signs in, or creates the account of a new e-mail.
// Shows only when the API has a Google client configured, and not inside other apps' browsers,
// where Google refuses to sign in. Emits "success" with the API response (as /auth/sign_in does).
export default {
  name: "ComponentGoogleButton",
  props: {
    where: { type: String, default: "login" },
    referralCode: { type: String, default: "" },
    planIntent: { type: String, default: "" },
  },
  emits: ["success"],
  data() {
    return { clientId: null, inApp: isInAppBrowser(), error: "" };
  },
  async mounted() {
    if (this.inApp) return;
    try {
      const { data } = await this.axios.get("/api/v1/auth/google_config", { skipGlobalErrorHandler: true });
      this.clientId = data.client_id || null;
      if (!this.clientId) return;
      await loadGoogle();
      await this.$nextTick();
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.onCredential,
        ux_mode: "popup",
        use_fedcm_for_button: true,
        context: this.where === "signup" ? "signup" : "signin",
      });
      const width = Math.min(400, (this.$refs.button && this.$refs.button.offsetWidth) || 320);
      window.google.accounts.id.renderButton(this.$refs.button, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "rectangular",
        text: this.where === "signup" ? "signup_with" : "continue_with",
        logo_alignment: "center",
        locale: "pt-BR",
        width,
      });
    } catch (e) {
      this.clientId = null;
    }
  },
  methods: {
    async onCredential({ credential }) {
      this.error = "";
      const attribution = getAttribution() || {};
      const q = this.$route.query;
      try {
        const response = await this.axios.post(
          "/api/v1/auth/google",
          {
            credential,
            ref: this.referralCode || q.ref || undefined,
            plan_intent: this.planIntent || undefined,
            attribution: {
              utm_source: q.utm_source || attribution.utm_source,
              utm_medium: q.utm_medium || attribution.utm_medium,
              utm_campaign: q.utm_campaign || attribution.utm_campaign,
              utm_content: q.utm_content || attribution.utm_content,
              ab: q.ab || attribution.ab,
            },
          },
          { skipGlobalErrorHandler: true },
        );
        if (response.data && response.data.new_account) {
          track("sign_up", { method: "google", where: this.where, ...attribution });
        } else {
          track("login", { method: "google", where: this.where });
        }
        this.$emit("success", response);
      } catch (e) {
        const data = e.response && e.response.data;
        this.error = (data && data.errors && data.errors[0]) || "Não conseguimos entrar com o Google. Tente de novo ou use e-mail e senha.";
        track("google_signin_failed", { where: this.where, status: (e.response && e.response.status) || 0 });
      }
    },
  },
};
</script>

<style scoped>
.google-signin { margin: 4px 0 14px; }
.google-button { display: flex; justify-content: center; min-height: 44px; }
.google-error { color: #c0392b; font-size: 0.88em; margin: 8px 0 0; text-align: center; }
.google-or { display: flex; align-items: center; gap: 10px; margin-top: 16px; color: #8a949c; font-size: 0.85em; }
.google-or::before, .google-or::after { content: ""; flex: 1; height: 1px; background: #e3e7ea; }
</style>
